<script setup lang="ts">
import { Head } from '@inertiajs/vue3';
import { Binoculars, BookOpen, Database, Search, Zap } from 'lucide-vue-next';
import { ref } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

defineOptions({
    layout: {
        breadcrumbs: [
            {
                title: 'Bibliothèque',
                href: '/discovery',
            },
        ],
    },
});

const email = ref('');
const submitted = ref(false);

function submitWaitlist() {
    if (!email.value.trim()) {
        return;
    }

    submitted.value = true;
}
</script>

<template>
    <Head title="Bibliothèque" />

    <div class="flex max-w-3xl flex-col gap-8 p-6">
        <!-- En-tête -->
        <div>
            <div class="mb-2 flex items-center gap-2">
                <h1 class="text-2xl font-semibold tracking-tight">
                    Bibliothèque de recherche
                </h1>
                <Badge variant="secondary" class="text-xs"
                    >Bientôt disponible</Badge
                >
            </div>
            <p class="max-w-xl text-sm text-muted-foreground">
                Recherchez dans la littérature scientifique mondiale et importez
                les articles pertinents directement dans vos projets de revue —
                sans quitter l'application.
            </p>
        </div>

        <!-- Barre de recherche désactivée -->
        <div class="relative">
            <Search
                class="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
                disabled
                placeholder="Rechercher dans PubMed, CrossRef, OpenAlex…"
                class="cursor-not-allowed pl-9 opacity-50"
            />
        </div>

        <!-- Ce qui arrive -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div
                class="space-y-2 rounded-xl border border-border bg-muted/30 p-4"
            >
                <Database class="h-5 w-5 text-brand" />
                <p class="text-sm font-semibold">Bases multiples</p>
                <p class="text-xs text-muted-foreground">
                    PubMed, CrossRef, OpenAlex, Semantic Scholar et plus.
                </p>
            </div>
            <div
                class="space-y-2 rounded-xl border border-border bg-muted/30 p-4"
            >
                <Zap class="h-5 w-5 text-brand" />
                <p class="text-sm font-semibold">Import en un clic</p>
                <p class="text-xs text-muted-foreground">
                    Ajoutez les articles trouvés directement à votre projet
                    actif.
                </p>
            </div>
            <div
                class="space-y-2 rounded-xl border border-border bg-muted/30 p-4"
            >
                <BookOpen class="h-5 w-5 text-brand" />
                <p class="text-sm font-semibold">Filtres avancés</p>
                <p class="text-xs text-muted-foreground">
                    Filtrez par année, langue, type d'étude, accès libre.
                </p>
            </div>
        </div>

        <!-- Waitlist -->
        <div class="space-y-3 rounded-xl border border-brand/20 bg-brand/5 p-5">
            <div class="flex items-center gap-2">
                <Binoculars class="h-5 w-5 text-brand" />
                <p class="text-sm font-semibold text-foreground">
                    Être prévenu à l'ouverture
                </p>
            </div>
            <p class="text-xs text-muted-foreground">
                La Bibliothèque de recherche est en cours de développement.
                Laissez votre email pour être notifié dès qu'elle sera
                disponible.
            </p>
            <template v-if="!submitted">
                <div class="flex gap-2">
                    <Input
                        v-model="email"
                        type="email"
                        placeholder="votre@email.com"
                        class="max-w-xs"
                        @keydown.enter="submitWaitlist"
                    />
                    <Button
                        size="sm"
                        class="bg-brand hover:bg-brand/90"
                        @click="submitWaitlist"
                    >
                        Me prévenir
                    </Button>
                </div>
            </template>
            <p v-else class="text-sm font-medium text-brand">
                ✓ Noté ! Vous serez informé à l'ouverture.
            </p>
        </div>
    </div>
</template>
